import React, { useEffect, useState } from "react";
import ReusableModal from "../../../../components/Modals/ReusableModal";
import Loading from "../../../../components/Loading";
import { useGetInsurancesMutation } from "../../../../features/patient/patientApiSlice";
import { selectUser } from "../../../../features/auth/authSlice";
import { useSelector } from "react-redux";
import Select from "react-select";

// Validation functions
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhoneNumber = (phone) => {
  const phoneRegex = /^\+?[\d\s-()]{10,}$/;
  return phoneRegex.test(phone);
};

const validateSSN = (ssn) => {
  const ssnRegex = /^\d{3}-?\d{2}-?\d{4}$/;
  return ssnRegex.test(ssn);
};

const validateZipCode = (zip) => {
  const zipRegex = /^\d{5}(-\d{4})?$/;
  return zipRegex.test(zip);
};

const PatientAddModal = ({
  isOpen,
  edit,
  onUpdate,
  onSave,
  setOpen,
  data,
  departments,
  providers,
  isCreating,
  isMember,
}) => {
  const [getInsurances] = useGetInsurancesMutation();
  const user = useSelector(selectUser);
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [insurances, setInsurances] = useState([]);
  const [patientData, setPatientData] = useState(
    data ?? {
      firstname: "",
      middlename: "",
      lastname: "",
      dob: "",
      sex: "M",
      address2: "",
      address1: "",
      city: "",
      state: "",
      zip: "",
      country: "",
      departmentid: "",
      email: "",
      homephone: "",
      mobilephone: "",
      notes: "",
      primaryproviderid: "",
      driverslicensenumber: "",
      ssn: "",
      facilityname: "",
      facilitytype: "",
      facilityphone: "",
    }
  );
  const [insuranceData, setInsuranceData] = useState({
    insurancepackageid: "",
    insuranceidnumber: "",
    sequencenumber: "",
    expirationdate: "",
    issuedate: "",
    policynumber: "",
    updateappointments: "",
    lob: "",
    payer: "",
  });

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "email":
        if (value && !validateEmail(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "homephone":
      case "mobilephone":
        if (value && !validatePhoneNumber(value)) {
          error = "Please enter a valid phone number";
        }
        break;
      case "ssn":
        if (value && !validateSSN(value)) {
          error = "Please enter a valid SSN (XXX-XX-XXXX)";
        }
        break;
      case "zip":
        if (value && !validateZipCode(value)) {
          error = "Please enter a valid zip code";
        }
        break;
      case "firstname":
      case "lastname":
        if (!value.trim()) {
          error = "This field is required";
        }
        break;
      case "dob":
        if (!value) {
          error = "Date of birth is required";
        }
        break;
      case "sex":
        if (!value) {
          error = "Gender is required";
        }
        break;
      case "departmentid":
      case "primaryproviderid":
        if (!value || value === "NA") {
          error = "This field is required";
        }
        break;
      default:
        break;
    }

    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const tmpData = { ...patientData };
    tmpData[name] = value;
    setPatientData(tmpData);

    // Validate field
    const error = validateField(name, value);
    setFieldErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleInsuranceChange = (e) => {
    const { name, value } = e.target;
    const tmpData = { ...insuranceData };
    tmpData[name] = value;
    setInsuranceData(tmpData);

    // Validate insurance fields
    let error = "";
    if (name === "insuranceidnumber" && value && value.length < 3) {
      error = "Insurance ID number must be at least 3 characters";
    }
    setFieldErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const fetchInsurance = async () => {
    const { data } = await getInsurances({
      practiceId: user.organisation.practiceId,
    }).unwrap();
    setInsurances(data);
  };

  useEffect(() => {
    fetchInsurance();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = {};
    if (!patientData.firstname.trim())
      errors.firstname = "First name is required";
    if (!patientData.lastname.trim()) errors.lastname = "Last name is required";
    if (!patientData.dob) errors.dob = "Date of birth is required";
    if (!patientData.sex) errors.sex = "Gender is required";
    if (
      !patientData.primaryproviderid ||
      patientData.primaryproviderid === "NA"
    )
      errors.primaryproviderid = "Primary provider is required";
    if (!patientData.departmentid || patientData.departmentid === "NA")
      errors.departmentid = "Department is required";

    if (
      patientData.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(patientData.email)
    ) {
      errors.email = "Please enter a valid email address";
    }

    if (
      patientData.homephone &&
      !/^\+?[\d\s-()]{10,}$/.test(patientData.homephone)
    ) {
      errors.homephone = "Please enter a valid phone number";
    }

    if (
      patientData.mobilephone &&
      !/^\+?[\d\s-()]{10,}$/.test(patientData.mobilephone)
    ) {
      errors.mobilephone = "Please enter a valid phone number";
    }

    if (patientData.ssn && !/^\d{3}-?\d{2}-?\d{4}$/.test(patientData.ssn)) {
      errors.ssn = "Please enter a valid SSN (XXX-XX-XXXX)";
    }

    if (patientData.zip && !/^\d{5}(-\d{4})?$/.test(patientData.zip)) {
      errors.zip = "Please enter a valid zip code";
    }

    if (
      !isMember &&
      insuranceData.insuranceidnumber &&
      insuranceData.insuranceidnumber.length < 3
    ) {
      errors.insuranceidnumber =
        "Insurance ID number must be at least 3 characters";
    }

    if (Object.keys(errors).length > 0) {
      setErrorMsg("Please fix the validation errors before proceeding");
      setFieldErrors(errors);
      return;
    }

    try {
      const department = departments?.find(
        (department) => department.department_id == patientData.departmentid
      );
      const patientUpdateData = {
        ...patientData,
        careProgram: department?.name?.slice(-3),
      };
      if (edit) {
        await onUpdate(e, patientData);
      } else {
        await onSave(e, patientUpdateData, insuranceData);
      }
      setOpen(false);
    } catch (error) {
      setErrorMsg(error.message);
    }
  };

  const providersData = providers?.map((provider) => ({
    value: String(provider.provider_ehr_id),
    label: provider.name,
  }));

  return (
    <ReusableModal
      open={isOpen}
      title={edit ? "Edit patient" : isMember ? "Add member" : "Add patient"}
      onClose={() => {
        setOpen(false);
      }}
    >
      <Loading loading={isCreating} />
      <div className="flex flex-col items-center min-w-[480px]">
        <div className="h-[1px] w-full bg-primarygrayborder" />
        <div className="flex flex-col gap-4 p-6 overflow-auto max-h-[calc(100vh-250px)]">
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-full gap-2">
              <label
                htmlFor="firstname"
                className="m-0 text-base font-semibold"
              >
                First Name
              </label>
              <input
                type="text"
                id="firstname"
                name="firstname"
                placeholder="First name"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.firstname
                    ? "border-red-500"
                    : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.firstname}
                onChange={handleChange}
              />
              {fieldErrors.firstname && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.firstname}
                </span>
              )}
            </div>
            <div className="flex flex-col w-full gap-2">
              <label
                htmlFor="middlename"
                className="m-0 text-base font-semibold"
              >
                Middle Name
              </label>
              <input
                type="text"
                id="middlename"
                name="middlename"
                placeholder="Middle name"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.middlename
                    ? "border-red-500"
                    : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.middlename}
                onChange={handleChange}
              />
              {fieldErrors.middlename && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.middlename}
                </span>
              )}
            </div>
            <div className="flex flex-col w-full gap-2">
              <label htmlFor="lastname" className="m-0 text-base font-semibold">
                Last Name
              </label>
              <input
                type="text"
                id="lastname"
                name="lastname"
                placeholder="Last name"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.lastname ? "border-red-500" : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.lastname}
                onChange={handleChange}
              />
              {fieldErrors.lastname && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.lastname}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-full gap-2">
              <label htmlFor="dob" className="m-0 text-base font-semibold">
                DOB
              </label>
              <input
                type="date"
                id="dob"
                name="dob"
                placeholder="MM/DD/YYYY"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.dob ? "border-red-500" : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.dob}
                onChange={handleChange}
              />
              {fieldErrors.dob && (
                <span className="text-red-500 text-sm">{fieldErrors.dob}</span>
              )}
            </div>
            <div className="flex flex-col w-full gap-2">
              <label htmlFor="sex" className="m-0 text-base font-semibold">
                Gender
              </label>
              <select
                name="sex"
                id="sex"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.sex ? "border-red-500" : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.sex}
                onChange={handleChange}
              >
                <option value="M">M</option>
                <option value="F">F</option>
              </select>
              {fieldErrors.sex && (
                <span className="text-red-500 text-sm">{fieldErrors.sex}</span>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-full gap-2">
              <label
                htmlFor="departmentid"
                className="m-0 text-base font-semibold"
              >
                Primary Department
              </label>
              <Select
                name="departmentid"
                id="departmentid"
                options={departments
                  ?.map((department) => ({
                    value: String(department.department_id),
                    label: department.name,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label))}
                className="w-full"
                value={
                  departments
                    ?.map((department) => ({
                      value: String(department.department_id),
                      label: department.name,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))
                    .find(
                      (option) => option.value == patientData?.departmentid
                    ) || null
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "departmentid",
                      value: e ? e.value : "",
                    },
                  })
                }
                placeholder="Select Department"
                isClearable
              />
              {fieldErrors.departmentid && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.departmentid}
                </span>
              )}
            </div>
            <div className="flex flex-col w-full gap-2">
              <label
                htmlFor="primaryproviderid"
                className="m-0 text-base font-semibold"
              >
                Primary Provider
              </label>
              <Select
                name="primaryproviderid"
                id="primaryproviderid"
                options={providersData}
                className={`w-full`}
                value={
                  providersData?.find(
                    (provider) =>
                      provider.value === patientData?.primaryproviderid
                  ) || null
                }
                onChange={(e) => {
                  setPatientData({
                    ...patientData,
                    primaryproviderid: e ? e.value : "",
                  });
                }}
                isClearable
              />
              {fieldErrors.primaryproviderid && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.primaryproviderid}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-full gap-2">
              <label
                htmlFor="homephone"
                className="m-0 text-base font-semibold"
              >
                Home phone
              </label>
              <input
                type="text"
                id="homephone"
                name="homephone"
                placeholder="Home phone"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.homephone
                    ? "border-red-500"
                    : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.homephone}
                onChange={handleChange}
              />
              {fieldErrors.homephone && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.homephone}
                </span>
              )}
            </div>
            <div className="flex flex-col w-full gap-2">
              <label
                htmlFor="mobilephone"
                className="m-0 text-base font-semibold"
              >
                Mobile phone
              </label>
              <input
                type="text"
                id="mobilephone"
                name="mobilephone"
                placeholder="Mobile phone"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.mobilephone
                    ? "border-red-500"
                    : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.mobilephone}
                onChange={handleChange}
              />
              {fieldErrors.mobilephone && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.mobilephone}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-full gap-2">
              <label htmlFor="email" className="m-0 text-base font-semibold">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Email"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.email ? "border-red-500" : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.email}
                onChange={handleChange}
              />
              {fieldErrors.email && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.email}
                </span>
              )}
            </div>
            <div className="flex flex-col w-full gap-2">
              <label htmlFor="ssn" className="m-0 text-base font-semibold">
                SSN
              </label>
              <input
                type="text"
                id="ssn"
                name="ssn"
                placeholder="SSN"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.ssn ? "border-red-500" : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.ssn}
                onChange={handleChange}
              />
              {fieldErrors.ssn && (
                <span className="text-red-500 text-sm">{fieldErrors.ssn}</span>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-full gap-2">
              <label
                htmlFor="driverslicensenumber"
                className="m-0 text-base font-semibold"
              >
                Driver License Number
              </label>
              <input
                type="text"
                id="driverslicensenumber"
                name="driverslicensenumber"
                placeholder="Driver License Number"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.driverslicensenumber
                    ? "border-red-500"
                    : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.driverslicensenumber}
                onChange={handleChange}
              />
              {fieldErrors.driverslicensenumber && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.driverslicensenumber}
                </span>
              )}
            </div>
            <div className="flex flex-col w-full gap-2">
              <label
                htmlFor="facilityname"
                className="m-0 text-base font-semibold"
              >
                Facility Name
              </label>
              <input
                type="text"
                id="facilityname"
                name="facilityname"
                placeholder="Facility Name"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.facilityname
                    ? "border-red-500"
                    : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.facilityname}
                onChange={handleChange}
              />
              {fieldErrors.facilityname && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.facilityname}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-1/2 gap-2">
              <label
                htmlFor="facilitytype"
                className="m-0 text-base font-semibold"
              >
                Facility Type
              </label>
              <input
                type="text"
                id="facilitytype"
                name="facilitytype"
                placeholder="Facility Type"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.facilitytype
                    ? "border-red-500"
                    : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.facilitytype}
                onChange={handleChange}
              />
              {fieldErrors.facilitytype && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.facilitytype}
                </span>
              )}
            </div>
            <div className="flex flex-col w-1/2  gap-2">
              <label
                htmlFor="facilityphone"
                className="m-0 text-base font-semibold"
              >
                Facility Phone
              </label>
              <input
                type="text"
                id="facilityphone"
                name="facilityphone"
                placeholder="Facility Phone"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.facilityphone
                    ? "border-red-500"
                    : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.facilityphone}
                onChange={handleChange}
              />
              {fieldErrors.facilityphone && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.facilityphone}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="address1" className="m-0 text-base font-semibold">
                Address 1
              </label>
              <input
                type="text"
                id="address1"
                name="address1"
                placeholder="Address 1"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.address1 ? "border-red-500" : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.address1}
                onChange={handleChange}
              />
              {fieldErrors.address1 && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.address1}
                </span>
              )}
            </div>
            <div className="flex flex-col w-1/2  gap-2">
              <label htmlFor="address2" className="m-0 text-base font-semibold">
                Address 2
              </label>
              <input
                type="text"
                id="address2"
                name="address2"
                placeholder="Address 2"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.address2 ? "border-red-500" : "border-primarygray"
                } rounded-md outline-none`}
                value={patientData?.address2}
                onChange={handleChange}
              />
              {fieldErrors.address2 && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.address2}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="city" className="m-0 text-base font-semibold">
                City
              </label>
              <input
                type="text"
                id="city"
                name="city"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.city ? "border-red-500" : "border-primarygray"
                } rounded-md outline-none`}
                placeholder="City"
                value={patientData?.city}
                onChange={handleChange}
              />
              {fieldErrors.city && (
                <span className="text-red-500 text-sm">{fieldErrors.city}</span>
              )}
            </div>
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="state" className="m-0 text-base font-semibold">
                State
              </label>
              <input
                type="text"
                id="state"
                name="state"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.state ? "border-red-500" : "border-primarygray"
                } rounded-md outline-none`}
                placeholder="State"
                value={patientData?.state}
                onChange={handleChange}
              />
              {fieldErrors.state && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.state}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="zip" className="m-0 text-base font-semibold">
                Zip
              </label>
              <input
                type="text"
                id="zip"
                name="zip"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.zip ? "border-red-500" : "border-primarygray"
                } rounded-md outline-none`}
                placeholder="Zip code"
                value={patientData?.zip}
                onChange={handleChange}
              />
              {fieldErrors.zip && (
                <span className="text-red-500 text-sm">{fieldErrors.zip}</span>
              )}
            </div>
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="country" className="m-0 text-base font-semibold">
                Country
              </label>
              <input
                type="text"
                id="country"
                name="country"
                className={`w-full p-2 border-[1px] ${
                  fieldErrors.country ? "border-red-500" : "border-primarygray"
                } rounded-md outline-none`}
                placeholder="Country"
                value={patientData?.country}
                onChange={handleChange}
              />
              {fieldErrors.country && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.country}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-full gap-2">
              <label htmlFor="notes" className="m-0 text-base font-semibold">
                Notes
              </label>
              <textarea
                type="text"
                id="notes"
                name="notes"
                className={`w-full p-2 border ${
                  fieldErrors.notes ? "border-red-500" : "border-primarygray"
                } rounded-md`}
                placeholder="Notes"
                value={patientData?.notes}
                onChange={handleChange}
                rows={4}
              />
              {fieldErrors.notes && (
                <span className="text-red-500 text-sm">
                  {fieldErrors.notes}
                </span>
              )}
            </div>
          </div>
          <>
            <div className="flex flex-row w-full">
              <div className="h-[1px] w-full bg-primarygrayborder" />
            </div>
            <div className="flex flex-row w-full gap-4">
              <div className="flex flex-col w-1/2 gap-2">
                <label
                  htmlFor="insuranceidnumber"
                  className="m-0 text-base font-semibold"
                >
                  Insurance ID Number
                </label>
                <input
                  type="text"
                  id="insuranceidnumber"
                  name="insuranceidnumber"
                  className={`w-full p-2 border-[1px] ${
                    fieldErrors.insuranceidnumber
                      ? "border-red-500"
                      : "border-primarygray"
                  } rounded-md outline-none`}
                  placeholder="Insurance ID Number"
                  value={insuranceData?.insuranceidnumber}
                  onChange={handleInsuranceChange}
                />
                {fieldErrors.insuranceidnumber && (
                  <span className="text-red-500 text-sm">
                    {fieldErrors.insuranceidnumber}
                  </span>
                )}
              </div>
              <div className="flex flex-col w-1/2 gap-2">
                <label
                  htmlFor="sequencenumber"
                  className="m-0 text-base font-semibold"
                >
                  Sequence Number
                </label>
                <Select
                  name="sequencenumber"
                  id="sequencenumber"
                  options={[
                    { value: "1", label: "Primary" },
                    { value: "2", label: "Secondary" },
                  ].sort((a, b) => a.label.localeCompare(b.label))}
                  className="w-full"
                  value={
                    [
                      { value: "1", label: "Primary" },
                      { value: "2", label: "Secondary" },
                    ]
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .find(
                        (option) =>
                          option.value === insuranceData?.sequencenumber
                      ) || null
                  }
                  onChange={(e) =>
                    handleInsuranceChange({
                      target: {
                        name: "sequencenumber",
                        value: e ? e.value : "",
                      },
                    })
                  }
                  placeholder="Select Sequence Number"
                  isClearable
                />
                {fieldErrors.sequencenumber && (
                  <span className="text-red-500 text-sm">
                    {fieldErrors.sequencenumber}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-row w-full gap-4">
              <div className="flex flex-col w-1/2 gap-2">
                <label
                  htmlFor="insurancepackageid"
                  className="m-0 text-base font-semibold"
                >
                  Insurance Package ID
                </label>
                <Select
                  name="insurancepackageid"
                  id="insurancepackageid"
                  options={insurances
                    .map((insurance) => ({
                      value: insurance.insurancepackageid,
                      label: insurance.insurancepackageid,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                  className="w-full"
                  value={
                    insurances
                      .map((insurance) => ({
                        value: insurance.insurancepackageid,
                        label: insurance.insurancepackageid,
                      }))
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .find(
                        (option) =>
                          option.value === insuranceData?.insurancepackageid
                      ) || null
                  }
                  onChange={(e) =>
                    handleInsuranceChange({
                      target: {
                        name: "insurancepackageid",
                        value: e ? e.value : "",
                      },
                    })
                  }
                  placeholder="Select Insurance Package ID"
                  isClearable
                />
                {fieldErrors.insurancepackageid && (
                  <span className="text-red-500 text-sm">
                    {fieldErrors.insurancepackageid}
                  </span>
                )}
              </div>
              <div className="flex flex-col w-1/2 gap-2">
                <label
                  htmlFor="policynumber"
                  className="m-0 text-base font-semibold"
                >
                  Policy Number
                </label>
                <input
                  type="text"
                  id="policynumber"
                  name="policynumber"
                  className={`w-full p-2 border-[1px] ${
                    fieldErrors.policynumber
                      ? "border-red-500"
                      : "border-primarygray"
                  } rounded-md outline-none`}
                  placeholder="Policy Number"
                  value={insuranceData?.policynumber}
                  onChange={handleInsuranceChange}
                />
                {fieldErrors.policynumber && (
                  <span className="text-red-500 text-sm">
                    {fieldErrors.policynumber}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-row w-full gap-4">
              <div className="flex flex-col w-1/2 gap-2">
                <label
                  htmlFor="lob"
                  className="m-0 text-base font-semibold"
                >
                  LOB
                </label>
                <input
                  type="text"
                  id="lob"
                  name="lob"
                  className={`w-full p-2 border-[1px] ${
                    fieldErrors.lob ? "border-red-500" : "border-primarygray"
                  } rounded-md outline-none`}
                  placeholder="LOB"
                  value={insuranceData?.lob}
                  onChange={handleInsuranceChange}
                />
                {fieldErrors.lob && (
                  <span className="text-red-500 text-sm">
                    {fieldErrors.lob}
                  </span>
                )}
              </div>
              <div className="flex flex-col w-1/2 gap-2">
                <label
                  htmlFor="payer"
                  className="m-0 text-base font-semibold"
                >
                  Payer
                </label>
                <input
                  type="text"
                  id="payer"
                  name="payer"
                  className={`w-full p-2 border-[1px] ${
                    fieldErrors.payer
                      ? "border-red-500"
                      : "border-primarygray"
                  } rounded-md outline-none`}
                  placeholder="Payer"
                  value={insuranceData?.payer}
                  onChange={handleInsuranceChange}
                />
                {fieldErrors.payer && (
                  <span className="text-red-500 text-sm">
                    {fieldErrors.payer}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-row w-full gap-4">
              <div className="flex flex-col w-1/2 gap-2">
                <label
                  htmlFor="issuedate"
                  className="m-0 text-base font-semibold"
                >
                  Issue Date
                </label>
                <input
                  type="date"
                  id="issuedate"
                  name="issuedate"
                  className={`w-full p-2 border-[1px] ${
                    fieldErrors.issuedate
                      ? "border-red-500"
                      : "border-primarygray"
                  } rounded-md outline-none`}
                  placeholder="Issue Date"
                  value={insuranceData?.issuedate}
                  onChange={handleInsuranceChange}
                />
                {fieldErrors.issuedate && (
                  <span className="text-red-500 text-sm">
                    {fieldErrors.issuedate}
                  </span>
                )}
              </div>
              <div className="flex flex-col w-1/2 gap-2">
                <label
                  htmlFor="expirationdate"
                  className="m-0 text-base font-semibold"
                >
                  Expiration Date
                </label>
                <input
                  type="date"
                  id="expirationdate"
                  name="expirationdate"
                  className={`w-full p-2 border-[1px] ${
                    fieldErrors.expirationdate
                      ? "border-red-500"
                      : "border-primarygray"
                  } rounded-md outline-none`}
                  placeholder="Expiration Date"
                  value={insuranceData?.expirationdate}
                  onChange={handleInsuranceChange}
                />
                {fieldErrors.expirationdate && (
                  <span className="text-red-500 text-sm">
                    {fieldErrors.expirationdate}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-row w-full gap-4">
              <div className="flex flex-col w-1/2 gap-2">
                <label
                  htmlFor="updateappointments"
                  className="m-0 text-base font-semibold"
                >
                  Update Appointments
                </label>
                <select
                  name="updateappointments"
                  id="updateappointments"
                  className={`w-full p-2 border-[1px] ${
                    fieldErrors.updateappointments
                      ? "border-red-500"
                      : "border-primarygray"
                  } rounded-md outline-none`}
                  value={insuranceData?.updateappointments}
                  onChange={handleInsuranceChange}
                >
                  <option value="">Update insurance in Appointments?</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
                {fieldErrors.updateappointments && (
                  <span className="text-red-500 text-sm">
                    {fieldErrors.updateappointments}
                  </span>
                )}
              </div>
            </div>
          </>
        </div>
        {errorMsg && <div className="text-red-500 w-[500px]">{errorMsg}</div>}
        <div className="w-full d-flex justify-end mb-2 gap-4 px-6">
          <button
            className="w-36 font-semibold p-2 border-[2px] rounded-md bg-subtleblue text-primarygray border-primarygray whitespace-nowrap"
            onClick={async (e) => {
              e.preventDefault();
              setOpen(false);
            }}
          >
            Cancel
          </button>
          <button
            disabled={
              !patientData.firstname ||
              !patientData.lastname ||
              !patientData.primaryproviderid ||
              !patientData.departmentid ||
              !patientData.dob
            }
            className="w-36 disabled:opacity-40 disabled:cursor-not-allowed font-semibold p-2 border-[2px] rounded-md bg-subtleblue border-primaryblue text-primaryblue whitespace-nowrap"
            onClick={handleSave}
          >
            {edit ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </ReusableModal>
  );
};

export default PatientAddModal;
